import { useEffect, useState } from 'react';
import {
  RELATIVE_PRESETS,
  describeDateRange,
  resolveDateRangePreset,
  type DateRangePresetId,
  type DateRangeValue,
} from '../utils/dateRange';

const PILL_BASE =
  'px-2.5 py-1 text-xs font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1';
const PILL_ACTIVE = 'bg-blue-600 border-blue-600 text-white';
const PILL_INACTIVE = 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50';

interface DateRangeFilterProps {
  /** Unique per tab, so the three copies on this page don't share element ids. */
  idPrefix: string;
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
}

/**
 * One-click relative ranges (2d / 7d / 30d / 90d / All) with an explicit
 * start–end pair behind "Custom". See `../utils/dateRange` for the bounds each
 * preset resolves to.
 */
export function DateRangeFilter({ idPrefix, value, onChange }: DateRangeFilterProps) {
  // Custom bounds are edited as a draft and only handed up on submit. A date
  // input reports '' for a half-typed date, so propagating every keystroke
  // would fire one unbounded query per segment the user types.
  const [draftStart, setDraftStart] = useState(value.startDate);
  const [draftEnd, setDraftEnd] = useState(value.endDate);

  // Keep the draft in step while a preset is driving the window, so switching
  // to Custom starts from the range that is already on screen.
  useEffect(() => {
    if (value.preset !== 'custom') {
      setDraftStart(value.startDate);
      setDraftEnd(value.endDate);
    }
  }, [value.preset, value.startDate, value.endDate]);

  function selectPreset(preset: DateRangePresetId) {
    if (preset === 'custom') {
      // Carry the current window over unchanged: no bounds move, so no refetch.
      onChange({ preset: 'custom', startDate: value.startDate, endDate: value.endDate });
      return;
    }
    onChange({ preset, ...resolveDateRangePreset(preset) });
  }

  const customDirty = draftStart !== value.startDate || draftEnd !== value.endDate;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-gray-600 mr-0.5">Range</span>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Date range">
          {RELATIVE_PRESETS.map(preset => (
            <button
              key={preset.id}
              type="button"
              aria-label={preset.name}
              aria-pressed={value.preset === preset.id}
              onClick={() => selectPreset(preset.id)}
              className={`${PILL_BASE} ${value.preset === preset.id ? PILL_ACTIVE : PILL_INACTIVE}`}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            aria-label="All time"
            aria-pressed={value.preset === 'all'}
            onClick={() => selectPreset('all')}
            className={`${PILL_BASE} ${value.preset === 'all' ? PILL_ACTIVE : PILL_INACTIVE}`}
          >
            All
          </button>
          <button
            type="button"
            aria-label="Custom date range"
            aria-pressed={value.preset === 'custom'}
            onClick={() => selectPreset('custom')}
            className={`${PILL_BASE} ${value.preset === 'custom' ? PILL_ACTIVE : PILL_INACTIVE}`}
          >
            Custom
          </button>
        </div>
        <span className="text-xs text-gray-500" data-testid={`${idPrefix}-range-summary`}>
          {describeDateRange(value)}
        </span>
      </div>

      {value.preset === 'custom' && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={e => {
            e.preventDefault();
            onChange({ preset: 'custom', startDate: draftStart, endDate: draftEnd });
          }}
        >
          <div>
            <label htmlFor={`${idPrefix}-start-date`} className="block text-xs font-medium text-gray-600">
              Start Date
            </label>
            <input
              type="date"
              id={`${idPrefix}-start-date`}
              className="mt-0.5 block text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              value={draftStart}
              onChange={e => setDraftStart(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-end-date`} className="block text-xs font-medium text-gray-600">
              End Date
            </label>
            <input
              type="date"
              id={`${idPrefix}-end-date`}
              className="mt-0.5 block text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              value={draftEnd}
              onChange={e => setDraftEnd(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={!customDirty}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </form>
      )}
    </div>
  );
}
